import { Action, createReducer, on } from '@ngrx/store';
import { OneTimePaymentActions, RecurringPaymentActions } from './actions';
import {
  PaymentState,
  ConfirmResult,
  PaymentElementFeatureState,
} from '../../types/payment-element/models';

// Literales seguros para evitar ensanchamiento a "string"
const ELEM = {
  idle: 'idle',
  mounting: 'mounting',
  mounted: 'mounted',
  error: 'error',
} as const;
const CONFIRM = {
  idle: 'idle',
  pending: 'pending',
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

const initialBranch: PaymentState = {
  containerId: null,
  clientSecret: null,
  active: false,
  elementStatus: ELEM.idle,
  confirmStatus: CONFIRM.idle,
  mountedFor: null,
  lastError: null,
};

// 👇 Helper para mapear el ConfirmResult a lastResult de forma segura
function toLastResult(
  result: ConfirmResult,
  prev: PaymentState['lastResult']
): PaymentState['lastResult'] {
  if (!result.ok) return prev;

  if (result.type === 'payment') {
    return {
      type: 'payment',
      intentId: result.intentId,
      status: result.status,
      paymentMethodId: result.paymentMethodId,
    };
  }

  // 'setup'
  return {
    type: 'setup',
    intentId: result.intentId,
    status: result.status,
  };
}

const oneTimeReducer = createReducer(
  initialBranch,
  on(OneTimePaymentActions.setClientSecret, (s, { clientSecret }) => ({
    ...s,
    clientSecret,
    confirmStatus: CONFIRM.idle,
  })),
  on(OneTimePaymentActions.setActive, (s, { active }) => ({
    ...s,
    active,
    confirmStatus: active ? s.confirmStatus : CONFIRM.idle,
  })),

  on(OneTimePaymentActions.viewEntered, (s, { containerId }) => ({
    ...s,
    containerId,
    confirmStatus: CONFIRM.idle,
  })),
  on(OneTimePaymentActions.viewExited, (s) => ({
    ...s,
    containerId: null,
    confirmStatus: CONFIRM.idle,
  })),

  on(OneTimePaymentActions.mountRequested, (s) => ({
    ...s,
    elementStatus: ELEM.mounting,
    lastError: null,
  })),
  on(OneTimePaymentActions.mounted, (s, { mountedFor }) => ({
    ...s,
    elementStatus: ELEM.mounted,
    mountedFor,
  })),
  on(OneTimePaymentActions.mountFailed, (s, { message }) => ({
    ...s,
    elementStatus: ELEM.error,
    lastError: message,
  })),
  on(OneTimePaymentActions.unmounted, (s) => ({
    ...s,
    elementStatus: ELEM.idle,
    mountedFor: null,
    confirmStatus: CONFIRM.idle,
  })),

  on(OneTimePaymentActions.confirmRequested, (s) => ({
    ...s,
    confirmStatus: CONFIRM.pending,
    lastError: null,
  })),
  on(OneTimePaymentActions.confirmReset, (s) => ({
    ...s,
    confirmStatus: CONFIRM.idle,
    lastError: null,
  })),
  on(OneTimePaymentActions.confirmSucceeded, (s, { result }) => ({
    ...s,
    confirmStatus: CONFIRM.succeeded,
    lastResult: toLastResult(result, s.lastResult),
  })),
  on(OneTimePaymentActions.confirmFailed, (s, { error }) => ({
    ...s,
    confirmStatus: CONFIRM.failed,
    lastError: error,
  }))
);

const recurringReducer = createReducer(
  initialBranch,
  on(RecurringPaymentActions.setClientSecret, (s, { clientSecret }) => ({
    ...s,
    clientSecret,
    confirmStatus: CONFIRM.idle,
  })),
  on(RecurringPaymentActions.setActive, (s, { active }) => ({
    ...s,
    active,
    confirmStatus: active ? s.confirmStatus : CONFIRM.idle,
  })),

  on(RecurringPaymentActions.viewEntered, (s, { containerId }) => ({
    ...s,
    containerId,
    confirmStatus: CONFIRM.idle,
  })),
  on(RecurringPaymentActions.viewExited, (s) => ({
    ...s,
    containerId: null,
    confirmStatus: CONFIRM.idle,
  })),

  on(RecurringPaymentActions.mountRequested, (s) => ({
    ...s,
    elementStatus: ELEM.mounting,
    lastError: null,
  })),
  on(RecurringPaymentActions.mounted, (s, { mountedFor }) => ({
    ...s,
    elementStatus: ELEM.mounted,
    mountedFor,
  })),
  on(RecurringPaymentActions.mountFailed, (s, { message }) => ({
    ...s,
    elementStatus: ELEM.error,
    lastError: message,
  })),
  on(RecurringPaymentActions.unmounted, (s) => ({
    ...s,
    elementStatus: ELEM.idle,
    mountedFor: null,
    confirmStatus: CONFIRM.idle,
  })),

  on(RecurringPaymentActions.confirmRequested, (s) => ({
    ...s,
    confirmStatus: CONFIRM.pending,
    lastError: null,
  })),
  on(RecurringPaymentActions.confirmReset, (s) => ({
    ...s,
    confirmStatus: CONFIRM.idle,
    lastError: null,
  })),
  on(RecurringPaymentActions.confirmSucceeded, (s, { result }) => ({
    ...s,
    confirmStatus: CONFIRM.succeeded,
    lastResult: toLastResult(result, s.lastResult),
  })),
  on(RecurringPaymentActions.confirmFailed, (s, { error }) => ({
    ...s,
    confirmStatus: CONFIRM.failed,
    lastError: error,
  }))
);

/** Reducer raíz único para provideStore({ paymentElement: PaymentElementReducer }) */
export function PaymentElementReducer(
  state: PaymentElementFeatureState | undefined,
  action: Action
): PaymentElementFeatureState {
  return {
    oneTime: oneTimeReducer(state?.oneTime, action),
    recurring: recurringReducer(state?.recurring, action),
  };
}
